
import getNested from 'lodash-es/get';
import setNested from 'lodash-es/set';
import cloneDeep from 'lodash-es/cloneDeep';
import { apiCoordsToLatLng } from '@/shared/coordinate-utils';

import Vue from 'vue';
import L from 'leaflet';
import CanvasMapPopupContent from '@/base_components/map/CanvasMapPopupContent';


export default {
  namespaced: true,

  state: () => {
    return {
      mapInstance: null,
      mapBounds: null,
      mapComponentInstance: null,

      vessels: new Map(),
      vesselsLoaded: false,
      selectedVesselIMO: null,
      highlightedVesselIMO: null,
      selectedVesselDetails: {
        imo: null,
        popupData: {},
      },

      tracking: {},

      vesselsFilterParameters: {},
      vesselsRetrievingMethod: () => {
        console.warn('vessels retrieving method not specified!')
      },

      markers: new Map(),
      popupInstance: null,

      zoomInProgress: false,
      targetZoom: null,
      callbacks: {
        zoomStart: null,
        zoomEnd: null,
        moveEnd: null,
      },
    };
  },

  getters: {
    mapInstance(state) {
      return state.mapInstance;
    },

    vessels(state) {
      return state.vessels;
    },

    totalVesselsCount(state) {
      if(state.vesselsLoaded) {
        return state.vessels.size;
      } else {
        return null;
      }
    },

    vesselsInViewCount(state, getters) {
      return getters.visibleVessels.size;
    },

    visibleVessels(state, getters) {
      if(state.mapBounds && state.vesselsLoaded) {
        let visibleVessels = new Map();

        for(let vessel of getters.vessels.values()) {
          const coordinates = apiCoordsToLatLng(
            getNested(vessel, 'geometry.coordinates', [0, 0])
          );
          if(state.mapBounds.contains(coordinates)) {
            visibleVessels.set(vessel.imo, vessel);
          }
        }
        return visibleVessels;
      } else {
        return new Map();
      }
    },

    zoomInProgress(state) {
      return state.zoomInProgress;
    },
  },

  mutations: {
    setValue(state, [path, value]) {
      setNested(state, path, value);
    },

    setMapInstance(state, mapInstance) {
      state.mapInstance = mapInstance;
      state.mapBounds = mapInstance.getBounds();
    },

    setMapComponent(state, componentInstance) {
      state.mapComponentInstance = componentInstance;
    },

    setVesselsFilterParameters(state, apiData) {
      state.vesselsFilterParameters = cloneDeep(apiData);
    },

    setVesselsRetrievingMethod(state, method) {
      state.vesselsRetrievingMethod = method;
    },

    setSelectedVesselIMO(state, imo) {
      state.selectedVesselIMO = imo;
    },

    setHighlightedVesselIMO(state, imo) {
      state.highlightedVesselIMO = imo;
    },

    setSelectedVesselDetails(state, data) {
      state.selectedVesselDetails = data;
    },

    addVessel(state, vessel) {
      state.vessels.set(vessel.imo, vessel);
    },

    clearVessels(state) {
      state.vessels.clear();
      state.vesselsLoaded = false;
    },

    setMarker(state, parameters) {
      state.markers.set(
        parameters.vessel.imo,
        parameters.markerInstance,
      );
    },

    clearMarkers(state) {
      state.markers.clear();
    },
  },

  actions: {
    async retrieveVessels(context, apiData) {
      // apiData will be applied to apiData in a closure,
      // check "vesselsRetrievingMethod" definition
      return context.state.vesselsRetrievingMethod(apiData);
    },

    async cacheAllVessels(context) {
      context.commit('clearVessels');
      const apiParameters = {
        //  whole world
        boundingBox: {
          lowerLeft: [-180.0, -90.0],
          upperRight: [180.0, 90.0],
        },
        limit: 50000,
      };
      const vessels = await context.dispatch('retrieveVessels', apiParameters);
      vessels.forEach(vessel => { context.commit('addVessel', vessel) });
      context.commit('setValue', ['vesselsLoaded', true]);
    },

    addVesselMarker({state, commit, dispatch}, vessel) {
      if(!vessel.imo || state.markers.has(vessel.imo)) {
        return;
      }

      const coordinates = apiCoordsToLatLng(getNested(vessel, 'geometry.coordinates', [0, 0]));
      const iconOptions = {
        color: 'rgba(10, 200, 10, 0.0)',  // adjust alpha to see marker rings
        fillColor: 'transparent',
        radius: 8,
        vessel: {
          imo: vessel.imo,
        },
      };

      const markerInstance = L.circleMarker(coordinates, iconOptions)
        .on('click', () => {
          // bindPopup raises errors, so it's needed to handle click manually
          commit('setSelectedVesselIMO', vessel.imo);
          dispatch('openVesselPopup', vessel.imo);
        })
        .addTo(state.mapInstance);

      commit('setMarker', {vessel, markerInstance});
    },

    removeAllMarkers({state, commit}) {
      if(!state.markers.size) {
        return;
      }

      return new Promise(resolve => {
        state.markers.forEach(layer => {
          state.mapInstance.removeLayer(layer);
        });

        commit('clearMarkers');
        resolve();
      });
    },

    openVesselPopup({state, commit}, vesselIMO) {
      const vessel = state.vessels.get(vesselIMO);
      if(!vessel || getNested(state.popupInstance, 'options.vessel.imo') === vesselIMO) {
        return;
      }

      const coordinates = apiCoordsToLatLng(getNested(vessel, 'geometry.coordinates', [0, 0]));
      const popupInstance = L.popup({
        className: 'rows-map-popup',
        vessel: {imo: vesselIMO},
      }).setLatLng(coordinates);
      commit('setValue', ['popupInstance', popupInstance]);

      popupInstance.openOn(state.mapInstance);

      // https://css-tricks.com/creating-vue-js-component-instances-programmatically/
      const PopupComponentClass = Vue.extend(CanvasMapPopupContent);
      const popupContentInstance = new PopupComponentClass({
        // this way we have $store, $router, etc in manually mounted child component
        parent: state.mapComponentInstance,
        propsData: {
          title: 'Vessel details',
          itemId: vessel.imo,
          rowsBasic: vessel.popupData,
        }
      });
      // forward events to <canvas-base-map> parent component
      popupContentInstance.$on(
        'show-voyages',
        itemId => state.mapComponentInstance.$emit('show-voyages', itemId)
      );
      popupContentInstance.$on(
        'show-tracks',
        itemId => state.mapComponentInstance.$emit('show-tracks', itemId)
      );
      state.mapComponentInstance.$emit('vessel-popup-open', vesselIMO)

      // manually mount popup content as vue component
      popupContentInstance.$mount(popupInstance.getElement().querySelector('.leaflet-popup-content'));

      // destroy popup component on leaflet popup destroy
      popupInstance.on('remove', () => {
        commit('setValue', ['popupInstance', null]);
        state.mapComponentInstance.$emit('vessel-popup-close', vesselIMO)
        popupContentInstance.$destroy();
      });
    },

    async updateTracking({state, commit}, trackingData) {
      // for now we re-render all lines and points on each change
      getNested(state, 'tracking.currentLayers', new Set()).forEach(layer => layer.remove());

      const trackingLines = trackingData.lines;
      const palette = trackingData.palette;
      let currentLayers = new Set();
      let paletteIndex = 0;

      trackingData.vessels.forEach(({imo: vesselIMO}) => {
        const polyline = L.polyline(trackingLines[vesselIMO], {color: palette[paletteIndex]}).addTo(state.mapInstance);
        currentLayers.add(polyline);

        if(trackingData.showPoints) {
          trackingData.tracks[vesselIMO].forEach(point => {
            const marker = L.circleMarker(
              point.position,
              {
                color: palette[paletteIndex],
                fillOpacity: 1,
                radius: 3,
                vessel: { imo: vesselIMO }}
            ).addTo(state.mapInstance)
            .on('mouseover', event => {
              showTooltipHtmlElement(state.mapInstance._container, event.containerPoint, point.timestamp);
            }).on('mouseout', () => {
              deleteTooltipHtmlElement();
            });
            currentLayers.add(marker);
          });
        }

        paletteIndex += 1;
        if(paletteIndex >= palette.length) {
          paletteIndex = 0;
        }
      });

      commit('setValue', ['tracking', {...trackingData, currentLayers}]);
    },

    async bindEvents(context) {
      const zoomStartCallback = (evt) => {
        const targetZoom = getNested(evt, 'target._animateToZoom', null);
        context.commit('setValue', ['targetZoom', targetZoom]);
        context.commit('setValue', ['zoomInProgress', true]);
      };

      const zoomEndCallback = () => {
        context.commit('setValue', ['targetZoom', null]);
        context.commit('setValue', ['zoomInProgress', false]);
        context.commit('setValue', ['mapBounds', context.getters.mapInstance.getBounds()]);
      };

      const moveEndCallback = () => {
        context.commit('setValue', ['mapBounds', context.getters.mapInstance.getBounds()]);
      };

      context.commit('setValue', ['callbacks.zoomStart', zoomStartCallback]);
      context.commit('setValue', ['callbacks.zoomEnd', zoomEndCallback]);
      context.commit('setValue', ['callbacks.moveEnd', moveEndCallback]);

      const map = context.state.mapInstance
      map.on('zoomstart', zoomStartCallback);
      map.on('zoomend', zoomEndCallback);
      map.on('moveend', moveEndCallback);
    },

    async unbindEvents(context) {
      const map = context.state.mapInstance
      map.off('zoomstart', context.state.callbacks.zoomStart);
      map.off('zoomend', context.state.callbacks.zoomEnd);
      map.off('moveend', context.state.callbacks.moveEnd);
    },
  },
}

function showTooltipHtmlElement(mapHtmlElement, point, text) {
  let div = document.createElement('div');
  div.id = 'tracking-tooltip';
  div.innerHTML = `<div>${text}</div>`;
  div.classList.add('leaflet-tooltip');
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  mapHtmlElement.append(div);


  const divDimesions = div.getBoundingClientRect()
  const mapWidth = mapHtmlElement.offsetWidth;
  let xCoordinate;
  if(point.x + divDimesions.width < mapWidth) {
    xCoordinate = point.x + 12;
    div.classList.add('leaflet-tooltip-right');
  } else {
    xCoordinate = point.x - divDimesions.width - 12;
    div.classList.add('leaflet-tooltip-left');
  }

  div.style.left = `${xCoordinate}px`;
  div.style.top = `${point.y - Math.round(divDimesions.height/2)}px`;
  div.style.zIndex = '1000';
  div.style.visibility = 'visible';
}

function deleteTooltipHtmlElement() {
  const element = document.getElementById('tracking-tooltip');
  if(element) {
    element.outerHTML = '';
  }
}
